import React, { useState, useContext, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { UserContext } from '../userContext';
import { collection, addDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebaseConfig';
import {
  FaQuestion,
  FaPaperPlane,
  FaUpload,
  FaArrowLeft,
  FaExclamationCircle,
  FaCheck,
  FaSpinner,
  FaFilePdf,
  FaImage,
  FaUser,
  FaEnvelope,
  FaGraduationCap,
  FaCalendarAlt,
  FaLink,
  FaClock,
  FaHeart
} from 'react-icons/fa';
import './PerguntaAluno.css';

// Utilitários PDF.js para gerar thumbnail
async function ensurePdfJsLoaded() {
  if (window.pdfjsLib) return;
  await new Promise((resolve, reject) => {
    const s1 = document.createElement('script');
    s1.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
    s1.onload = resolve;
    s1.onerror = reject;
    document.head.appendChild(s1);
  });
  window.pdfjsLib.GlobalWorkerOptions.workerSrc =
    'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
}

async function renderPdfFirstPageToCanvas(file, maxW = 800) {
  await ensurePdfJsLoaded();
  const loadingTask = window.pdfjsLib.getDocument({ data: await file.arrayBuffer() });
  const pdf = await loadingTask.promise;
  const page = await pdf.getPage(1);

  const vp = page.getViewport({ scale: 1 });
  const scale = Math.min(maxW / vp.width, 600 / vp.height);
  const viewport = page.getViewport({ scale });

  const canvas = document.createElement('canvas');
  canvas.width = Math.floor(viewport.width);
  canvas.height = Math.floor(viewport.height);
  const ctx = canvas.getContext('2d');

  await page.render({ canvasContext: ctx, viewport }).promise;
  return canvas;
}

function blobFromCanvas(canvas, type = 'image/jpeg', quality = 0.9) {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

function PerguntaAluno() {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();
  const fileInputRef = useRef(null);

  // Estados do formulário
  const [formData, setFormData] = useState({
    nomeCompleto: user?.name || '',
    email: user?.email || '',
    tipoSolicitacao: 'duvida',
    disciplina: '',
    descricao: '',
    semestre: '',
    curso: '',
    arquivo: null,
    linkRelacionado: '',
    urgencia: 'normal',
    formatoResposta: []
  });

  // Estados de controle
  const [enviando, setEnviando] = useState(false);
  const [mensagem, setMensagem] = useState('');
  const [previewArquivo, setPreviewArquivo] = useState('');
  const [arquivoInfo, setArquivoInfo] = useState(null);

  // Pegar o termo de busca da URL se vier da busca
  const termoBusca = new URLSearchParams(location.search).get('termo') || '';

  useEffect(() => {
    if (termoBusca) {
      setFormData(prev => ({
        ...prev,
        descricao: `Busquei por "${termoBusca}" mas não encontrei o que preciso.\n\n`
      }));
    }
  }, [termoBusca]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFormatoRespostaChange = (formato) => {
    setFormData(prev => ({
      ...prev,
      formatoResposta: prev.formatoResposta.includes(formato)
        ? prev.formatoResposta.filter(f => f !== formato)
        : [...prev.formatoResposta, formato]
    }));
  };

  const handleArquivoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFormData(prev => ({ ...prev, arquivo: file }));
    setArquivoInfo({
      nome: file.name,
      tamanho: (file.size / 1024 / 1024).toFixed(2) + ' MB',
      tipo: file.type
    });

    // Gerar preview
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setPreviewArquivo(e.target.result);
      reader.readAsDataURL(file);
    } else if (file.type === 'application/pdf') {
      try {
        const canvas = await renderPdfFirstPageToCanvas(file);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
        setPreviewArquivo(dataUrl);
      } catch (error) {
        console.error('Erro ao gerar preview do PDF:', error);
        setPreviewArquivo('');
      }
    } else {
      setPreviewArquivo('');
    }
  };

  const validarFormulario = () => {
    const erros = [];

    if (!formData.nomeCompleto.trim()) erros.push('Nome completo é obrigatório');
    if (!formData.email.trim()) erros.push('E-mail é obrigatório');
    if (!formData.disciplina.trim()) erros.push('Disciplina é obrigatória');
    if (!formData.descricao.trim()) erros.push('Descrição da pergunta é obrigatória');
    if (!formData.semestre) erros.push('Semestre é obrigatório');

    if (erros.length > 0) {
      setMensagem('Erros encontrados:\n• ' + erros.join('\n• '));
      return false;
    }

    return true;
  };

  const enviarPergunta = async (e) => {
    e.preventDefault();
    
    if (!validarFormulario()) return;

    setEnviando(true);
    setMensagem('');

    try {
      let arquivoURL = '';
      let thumbnailURL = '';

      // Upload do arquivo se existir
      if (formData.arquivo) {
        const arquivoRef = ref(storage, `perguntas_alunos/${Date.now()}_${formData.arquivo.name}`);
        const uploadSnap = await uploadBytes(arquivoRef, formData.arquivo);
        arquivoURL = await getDownloadURL(uploadSnap.ref);

        // Gerar thumbnail se for PDF
        if (formData.arquivo.type === 'application/pdf') {
          try {
            const canvas = await renderPdfFirstPageToCanvas(formData.arquivo);
            const thumbnailBlob = await blobFromCanvas(canvas, 'image/jpeg', 0.8);
            const thumbnailRef = ref(storage, `perguntas_thumbnails/${Date.now()}_thumb.jpg`);
            const thumbSnap = await uploadBytes(thumbnailRef, thumbnailBlob);
            thumbnailURL = await getDownloadURL(thumbSnap.ref);
          } catch (error) {
            console.error('Erro ao gerar thumbnail:', error);
          }
        } else if (formData.arquivo.type.startsWith('image/')) {
          thumbnailURL = arquivoURL;
        }
      }

      // Payload da pergunta
      const perguntaData = {
        // Informações pessoais
        nomeCompleto: formData.nomeCompleto,
        email: formData.email,
        
        // Detalhes da solicitação
        tipoSolicitacao: formData.tipoSolicitacao,
        disciplina: formData.disciplina,
        descricao: formData.descricao,
        
        // Contexto acadêmico
        semestre: formData.semestre,
        curso: formData.curso || 'Não informado',
        
        // Detalhes da questão
        arquivoURL,
        thumbnailURL,
        arquivoNome: formData.arquivo?.name || '',
        arquivoTipo: formData.arquivo?.type || '',
        linkRelacionado: formData.linkRelacionado,
        urgencia: formData.urgencia,
        
        // Preferências
        formatoResposta: formData.formatoResposta,
        
        // Metadados
        status: 'nova',
        lida: false,
        criadaEm: new Date(),
        atualizadaEm: new Date(),
        authorUid: user?.uid || null,
        termoBuscaOriginal: termoBusca || null,
        
        // Para compatibilidade com o sistema existente
        resposta: '',
        videoResposta: '',
        respondidaEm: null,
        respondidaPor: null
      };

      await addDoc(collection(db, 'perguntas_alunos'), perguntaData);

      setMensagem('✅ Pergunta enviada com sucesso! Nossa equipe entrará em contato em breve.');
      
      // Limpar formulário
      setFormData({
        nomeCompleto: user?.name || '',
        email: user?.email || '',
        tipoSolicitacao: 'duvida',
        disciplina: '',
        descricao: '',
        semestre: '',
        curso: '',
        arquivo: null,
        linkRelacionado: '',
        urgencia: 'normal',
        formatoResposta: []
      });
      setPreviewArquivo('');
      setArquivoInfo(null);
      if (fileInputRef.current) fileInputRef.current.value = '';

      // Redirecionar após 3 segundos
      setTimeout(() => {
        navigate('/');
      }, 3000);

    } catch (error) {
      console.error('Erro ao enviar pergunta:', error);
      setMensagem('❌ Erro ao enviar pergunta. Tente novamente.');
    } finally {
      setEnviando(false);
    }
  };

  const getUrgenciaColor = (urgencia) => {
    switch (urgencia) {
      case 'muito_urgente': return '#dc2626';
      case 'urgente': return '#f59e0b';
      case 'normal': return '#10b981';
      default: return '#6b7280';
    }
  };

  const getUrgenciaText = (urgencia) => {
    switch (urgencia) {
      case 'muito_urgente': return 'Muito urgente (até 24h)';
      case 'urgente': return 'Urgente (até 3 dias)';
      case 'normal': return 'Normal (até 7 dias)';
      default: return 'Normal';
    }
  };

  return (
    <div className="pergunta-aluno-container">
      {/* Header */}
      <div className="pergunta-header">
        <button onClick={() => navigate(-1)} className="back-button">
          <FaArrowLeft /> Voltar
        </button>
        <div className="header-content">
          <h1>
            <FaQuestion /> Enviar Pergunta para Especialistas
          </h1>
          <p>
            Não encontrou o que procurava? Nossa equipe de especialistas está pronta para ajudar!
          </p>
          {termoBusca && (
            <div className="termo-busca-info">
              <FaExclamationCircle />
              <span>Resultado da busca por: "<strong>{termoBusca}</strong>"</span>
            </div>
          )}
        </div>
      </div>

      {/* Formulário */}
      <div className="pergunta-form-container">
        <form onSubmit={enviarPergunta} className="pergunta-form">
          {/* Informações Pessoais */}
          <div className="form-section">
            <h3>
              <FaUser /> Informações Pessoais
            </h3>
            <div className="form-row">
              <div className="form-group">
                <label>Nome Completo *</label>
                <input
                  type="text"
                  value={formData.nomeCompleto}
                  onChange={(e) => handleInputChange('nomeCompleto', e.target.value)}
                  placeholder="Seu nome completo"
                  required
                />
              </div>
              <div className="form-group">
                <label>E-mail *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="seu@email.com"
                  required
                />
              </div>
            </div>
          </div>

          {/* Detalhes da Solicitação */}
          <div className="form-section">
            <h3>
              <FaQuestion /> Detalhes da Solicitação
            </h3>
            <div className="form-row">
              <div className="form-group">
                <label>Tipo de Solicitação *</label>
                <select
                  value={formData.tipoSolicitacao}
                  onChange={(e) => handleInputChange('tipoSolicitacao', e.target.value)}
                  required
                >
                  <option value="duvida">Dúvida sobre conteúdo</option>
                  <option value="exercicio">Resolução de exercício</option>
                  <option value="prova">Prova resolvida</option>
                  <option value="material">Material complementar</option>
                  <option value="outro">Outro</option>
                </select>
              </div>
              <div className="form-group">
                <label>Disciplina/Matéria *</label>
                <input
                  type="text"
                  value={formData.disciplina}
                  onChange={(e) => handleInputChange('disciplina', e.target.value)}
                  placeholder="Ex: Cálculo I, Física, Programação..."
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label>Descrição da Pergunta/Questão *</label>
              <textarea
                value={formData.descricao}
                onChange={(e) => handleInputChange('descricao', e.target.value)}
                placeholder="Descreva detalhadamente sua dúvida ou questão..."
                rows={6}
                required
              />
            </div>
          </div>

          {/* Contexto Acadêmico */}
          <div className="form-section">
            <h3>
              <FaGraduationCap /> Contexto Acadêmico
            </h3>
            <div className="form-row">
              <div className="form-group">
                <label>Semestre Atual *</label>
                <select
                  value={formData.semestre}
                  onChange={(e) => handleInputChange('semestre', e.target.value)}
                  required
                >
                  <option value="">Selecione o semestre</option>
                  <option value="1">1º Semestre</option>
                  <option value="2">2º Semestre</option>
                  <option value="3">3º Semestre</option>
                  <option value="4">4º Semestre</option>
                  <option value="5">5º Semestre</option>
                  <option value="6">6º Semestre</option>
                  <option value="7">7º Semestre</option>
                  <option value="8">8º Semestre</option>
                  <option value="outro">Outro</option>
                </select>
              </div>
              <div className="form-group">
                <label>Curso</label>
                <input
                  type="text"
                  value={formData.curso}
                  onChange={(e) => handleInputChange('curso', e.target.value)}
                  placeholder="Ex: Engenharia, Administração..."
                />
              </div>
            </div>
          </div>

          {/* Detalhes da Questão */}
          <div className="form-section">
            <h3>
              <FaUpload /> Detalhes da Questão
            </h3>
            
            <div className="form-group">
              <label>Anexar Arquivo (PDF, PNG, JPG)</label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.png,.jpg,.jpeg"
                onChange={handleArquivoChange}
              />
              <small className="form-help">
                Anexe prints da questão, exercício ou material de apoio
              </small>
              
              {arquivoInfo && (
                <div className="arquivo-info">
                  <div className="arquivo-detalhes">
                    {formData.arquivo?.type === 'application/pdf' ? (
                      <FaFilePdf className="arquivo-icon pdf" />
                    ) : (
                      <FaImage className="arquivo-icon image" />
                    )}
                    <div>
                      <p><strong>{arquivoInfo.nome}</strong></p>
                      <p>{arquivoInfo.tamanho}</p>
                    </div>
                  </div>
                  {previewArquivo && (
                    <div className="arquivo-preview">
                      <img src={previewArquivo} alt="Preview do arquivo" />
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="form-group">
              <label>Link Relacionado</label>
              <input
                type="url"
                value={formData.linkRelacionado}
                onChange={(e) => handleInputChange('linkRelacionado', e.target.value)}
                placeholder="Cole aqui links do AVA, YouTube ou outros materiais"
              />
              <small className="form-help">
                <FaLink /> Links para AVA, vídeos do YouTube ou outros materiais relacionados
              </small>
            </div>

            <div className="form-group">
              <label>Urgência</label>
              <div className="urgencia-options">
                {[
                  { value: 'normal', label: 'Normal (até 7 dias)' },
                  { value: 'urgente', label: 'Urgente (até 3 dias)' },
                  { value: 'muito_urgente', label: 'Muito urgente (até 24h)' }
                ].map(opcao => (
                  <label key={opcao.value} className="urgencia-option">
                    <input
                      type="radio"
                      name="urgencia"
                      value={opcao.value}
                      checked={formData.urgencia === opcao.value}
                      onChange={(e) => handleInputChange('urgencia', e.target.value)}
                    />
                    <span 
                      className="urgencia-label"
                      style={{ borderColor: getUrgenciaColor(opcao.value) }}
                    >
                      <FaClock style={{ color: getUrgenciaColor(opcao.value) }} />
                      {opcao.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Preferências */}
          <div className="form-section">
            <h3>
              <FaHeart /> Preferências de Resposta
            </h3>
            <div className="form-group">
              <label>Formato de Resposta Preferido (pode marcar vários)</label>
              <div className="formato-resposta-options">
                {[
                  { value: 'videoaula', label: 'Videoaula' },
                  { value: 'material_escrito', label: 'Material escrito' },
                  { value: 'resumo', label: 'Resumo' },
                  { value: 'exercicio_resolvido', label: 'Exercício resolvido' }
                ].map(formato => (
                  <label key={formato.value} className="formato-option">
                    <input
                      type="checkbox"
                      checked={formData.formatoResposta.includes(formato.value)}
                      onChange={() => handleFormatoRespostaChange(formato.value)}
                    />
                    <span>{formato.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Mensagem de status */}
          {mensagem && (
            <div className={`mensagem-status ${mensagem.includes('✅') ? 'sucesso' : 'erro'}`}>
              {mensagem}
            </div>
          )}

          {/* Botão de envio */}
          <div className="form-actions">
            <button 
              type="submit" 
              disabled={enviando}
              className="btn-enviar-pergunta"
            >
              {enviando ? (
                <>
                  <FaSpinner className="spinner" /> Enviando...
                </>
              ) : (
                <>
                  <FaPaperPlane /> Enviar Pergunta
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default PerguntaAluno;